<template>
    <router-link
        :to="{
            name: 'campaigns',
        }"
    >
        <font-awesome-icon
            icon="fa-solid fa-arrow-left"
            class="mr-1 dark:text-white"
            aria-hidden="true"
        />
        Voltar
    </router-link>
    <div class="my-3 flex flex-col">
        <form class="space-y-6" @submit.prevent="submit">
            <!-- Seção de informações básicas da campanha -->
            <div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
                <header class="flex items-center gap-2">
                    <span
                        class="mb-4 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Campanha ativa?</span
                    >
                    <form-toggle v-model="form.is_active" :loading="loading" />
                </header>
                <div class="grid grid-cols-12 gap-6">
                    <div class="col-span-6">
                        <label
                            class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                            for="start_date"
                            >Data de Início</label
                        >
                        <input
                            v-model="form.start_date"
                            id="start_date"
                            type="date"
                            name="start_date"
                            class="text-input mt-1 block w-full"
                        />
                    </div>
                    <div class="col-span-6">
                        <label
                            class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                            for="end_date"
                            >Data de Fim</label
                        >
                        <input
                            v-model="form.end_date"
                            id="end_date"
                            type="date"
                            name="end_date"
                            class="text-input mt-1 block w-full"
                        />
                    </div>
                    <div class="col-span-12">
                        <label
                            class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                            for="comments"
                            >Comentários</label
                        >
                        <textarea
                            v-model="form.comments"
                            id="comments"
                            name="comments"
                            rows="3"
                            class="text-input mt-1 block w-full"
                            placeholder="Comentários sobre a campanha..."
                        ></textarea>
                    </div>

                    <!-- Banner Desktop -->
                    <div class="col-span-6">
                        <label
                            class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Banner desktop (4774 x 633)
                        </label>
                        <div
                            class="rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700"
                        >
                            <file-pond
                                v-if="!form.desktop_image"
                                name="desktop_image"
                                ref="pondDesktop"
                                class-name="my-pond mt-1 block w-full"
                                label-idle="Arraste ou clique para adicionar arquivos para o banner desktop..."
                                :allow-multiple="false"
                                accepted-file-types="image/png"
                                :server="{
                                    process: (
                                        fieldName,
                                        file,
                                        metadata,
                                        load,
                                        error,
                                        progress,
                                        abort,
                                    ) => {
                                        this.uploadFile(
                                            fieldName,
                                            file,
                                            metadata,
                                            load,
                                            error,
                                            progress,
                                            abort,
                                            'desktop',
                                            4774,
                                            633,
                                        );
                                    },
                                }"
                            />
                            <div v-if="form.desktop_image" class="mt-2">
                                <div class="relative">
                                    <img
                                        :src="form.desktop_image"
                                        class="h-auto max-w-full rounded"
                                    />
                                    <button
                                        @click="replaceImage('desktop')"
                                        type="button"
                                        class="absolute right-2 top-2 z-10 inline-flex items-center justify-center rounded-md bg-blue-500 px-2 py-1 text-xs font-semibold text-white shadow hover:bg-blue-400"
                                    >
                                        <CheckCircleIcon class="mr-1 h-3 w-3" />
                                        Substituir
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Banner Mobile -->
                    <div class="col-span-6">
                        <label
                            class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Banner mobile (646 x 180)
                        </label>
                        <div
                            class="rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700"
                        >
                            <file-pond
                                v-if="!form.mobile_image"
                                name="mobile_image"
                                ref="pondMobile"
                                class-name="my-pond mt-1 block w-full"
                                label-idle="Arraste ou clique para adicionar arquivos para o banner mobile..."
                                :allow-multiple="false"
                                accepted-file-types="image/png"
                                :server="{
                                    process: (
                                        fieldName,
                                        file,
                                        metadata,
                                        load,
                                        error,
                                        progress,
                                        abort,
                                    ) => {
                                        this.uploadFile(
                                            fieldName,
                                            file,
                                            metadata,
                                            load,
                                            error,
                                            progress,
                                            abort,
                                            'mobile',
                                            646,
                                            180,
                                        );
                                    },
                                }"
                            />
                            <div v-if="form.mobile_image" class="mt-2">
                                <div class="relative">
                                    <img
                                        :src="form.mobile_image"
                                        class="h-auto max-w-full rounded"
                                    />
                                    <button
                                        @click="replaceImage('mobile')"
                                        type="button"
                                        class="absolute right-2 top-2 z-10 inline-flex items-center justify-center rounded-md bg-blue-500 px-2 py-1 text-xs font-semibold text-white shadow hover:bg-blue-400"
                                    >
                                        <CheckCircleIcon class="mr-1 h-3 w-3" />
                                        Substituir
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Botão para adicionar nova oferta -->
            <div class="flex justify-end">
                <button
                    @click.prevent="addOffer"
                    type="button"
                    class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500"
                >
                    <CheckCircleIcon class="mr-1 h-5 w-5" />
                    Nova Oferta
                </button>
            </div>

            <!-- Seção de ofertas -->
            <div
                v-for="(offer, index) in form.offers"
                :key="index"
                class="rounded-lg bg-white p-6 shadow dark:bg-gray-800"
            >
                <div class="mb-4 flex items-center justify-between">
                    <h3
                        class="text-lg font-medium text-gray-900 dark:text-white"
                    >
                        Oferta {{ index + 1 }}
                    </h3>
                    <button
                        v-if="form.offers.length > 1"
                        @click.prevent="removeOffer(index)"
                        type="button"
                        class="inline-flex items-center justify-center rounded-md bg-red-500 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-red-400"
                    >
                        <MinusCircleIcon class="mr-1 h-5 w-5" />
                        Remover
                    </button>
                </div>

                <div class="grid grid-cols-12 gap-6">
                    <div class="col-span-12 md:col-span-6">
                        <label
                            class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Níveis
                        </label>
                        <select
                            v-model="offer.level_ids"
                            class="text-input"
                        >
                            <option
                                v-for="level in levels"
                                :value="level.value"
                                :key="level.value"
                            >
                                {{ level.label }}
                            </option>
                        </select>
                    </div>

                    <div class="col-span-6">
                        <label
                            class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                            for="product_id"
                            >Oferta</label
                        >
                        <multiselect
                            v-model="offer.selectedOffer"
                            :options="listOffers"
                            :custom-label="(option) => option.name"
                            placeholder="Selecione uma oferta..."
                            :searchable="true"
                            :allow-empty="true"
                            :close-on-select="true"
                            :show-labels="false"
                            track-by="id"
                            label="name"
                            @select="
                                (selectedOption) => {
                                    offer.offer_id = selectedOption
                                        ? selectedOption.id
                                        : null;
                                    offer.selectedOffer = selectedOption;
                                }
                            "
                            @remove="
                                () => {
                                    offer.offer_id = null;
                                    offer.selectedOffer = null;
                                }
                            "
                        />
                    </div>
                </div>
            </div>

            <!-- Botão de submissão -->
            <div class="flex justify-end">
                <submit-button :loading="loading" class="ml-auto">{{
                    loading
                        ? "Salvando..."
                        : isEditing
                          ? "Atualizar Campanha"
                          : "Criar Campanha"
                }}</submit-button>
            </div>
        </form>
    </div>
</template>

<script>
import { TransitionRoot } from "@headlessui/vue";
import { CheckCircleIcon, MinusCircleIcon } from "@heroicons/vue/24/outline";
import { mapWritableState } from "pinia";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { useToast } from "vue-toastification";
import FormToggle from "../../components/forms/FormToggle.vue";
import SubmitButton from "../../components/forms/SubmitButton.vue";
import VueSelect from "../../components/forms/VueSelect.vue";
import BackButton from "../../components/layouts/navigation/BackButton.vue";
import NavTabs from "../../components/layouts/NavTabs.vue";
import { Store } from "../../stores/store.js";

import FilePondPluginImagePreview from "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.esm.js";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
import "filepond/dist/filepond.min.css";
import vueFilePond from "vue-filepond";

const FilePond = vueFilePond(FilePondPluginImagePreview);

export default {
    components: {
        TransitionRoot,
        BackButton,
        NavTabs,
        CheckCircleIcon,
        MinusCircleIcon,
        SubmitButton,
        FormToggle,
        VueSelect,
        FilePond,
        Multiselect,
    },
    props: {
        id: {
            type: [String, Number],
            default: null,
        },
    },

    data() {
        return {
            loading: false,
            isEditing: false,
            selectedOffer: null,
            form: {
                start_date: "",
                end_date: "",
                comments: "",
                is_active: true,
                desktop_image: null,
                desktop_file: null,
                mobile_image: null,
                mobile_file: null,
                offers: [
                    {
                        offer_id: null,
                        level_ids: [],
                        status: "enabled",
                    },
                ],
            },
            listOffers: [],
            levels: [],
            campaign: null,
            errors: {},
        };
    },

    computed: {
        ...mapWritableState(Store, ["storeCampaign", "permissions"]),
        availableLevels() {
            return (offerIndex) => {
                const usedLevelIds = [];

                // Coleta todos os IDs de levels já utilizados em outras ofertas
                this.form.offers.forEach((offer, index) => {
                    if (index !== offerIndex && offer.level_ids) {
                        offer.level_ids.forEach((level) => {
                            usedLevelIds.push(level.value);
                        });
                    }
                });

                // Filtra os levels disponíveis baseado nos values (IDs) não utilizados
                return this.levels.filter(
                    (level) => !usedLevelIds.includes(level.value),
                );
            };
        },
    },

    watch: {
        listOffers: {
            handler(newOffers) {
                if (newOffers && newOffers.length > 0 && this.isEditing) {
                    this.$nextTick(() => {
                        this.form.offers.forEach((offer) => {
                            if (offer.offer_id && !offer.selectedOffer) {
                                offer.selectedOffer =
                                    newOffers.find(
                                        (listOffer) =>
                                            listOffer.id === offer.offer_id,
                                    ) || null;
                            }
                        });
                    });
                }
            },
            immediate: true,
        },
    },

    setup() {
        const toast = useToast();
        return { toast };
    },

    methods: {
        goBack() {
            this.$router.push({ name: "campaigns" });
        },

        addOffer() {
            this.form.offers.push({
                offer_id: null,
                selectedOffer: null,
                level_ids: [],
                status: "enabled",
            });
        },

        removeOffer(index) {
            this.form.offers.splice(index, 1);
            this.clearErrors();
        },

        handleFileUpload(event, type) {
            const file = event.target.files[0];
            if (!file) return;

            if (!file.type.startsWith("image/")) {
                this.toast.error(
                    "Por favor, selecione um arquivo de imagem válido.",
                );
                return;
            }

            const imageUrl = URL.createObjectURL(file);

            if (type === "desktop") {
                this.form.desktop_file = file;
                this.form.desktop_image = imageUrl;
            }

            if (type === "mobile") {
                this.form.mobile_file = file;
                this.form.mobile_image = imageUrl;
            }
        },

        replaceImage(type) {
            // Limpar a imagem atual para mostrar o FilePond
            if (type === "desktop") {
                // Limpar URL temporária se existir
                if (
                    this.form.desktop_image &&
                    this.form.desktop_image.startsWith("blob:")
                ) {
                    URL.revokeObjectURL(this.form.desktop_image);
                }
                this.form.desktop_image = null;
                this.form.desktop_file = null;
            } else if (type === "mobile") {
                // Limpar URL temporária se existir
                if (
                    this.form.mobile_image &&
                    this.form.mobile_image.startsWith("blob:")
                ) {
                    URL.revokeObjectURL(this.form.mobile_image);
                }
                this.form.mobile_image = null;
                this.form.mobile_file = null;
            }

            this.toast.info(
                `Selecione uma nova imagem para ${type === "desktop" ? "desktop" : "mobile"}`,
            );
        },

        handleDeleteImage(type) {
            if (type === "desktop") {
                // Limpar URL temporária se existir
                if (
                    this.form.desktop_image &&
                    this.form.desktop_image.startsWith("blob:")
                ) {
                    URL.revokeObjectURL(this.form.desktop_image);
                }
                this.form.desktop_image = null;
                this.form.desktop_image_url = null;
                this.form.desktop_file = null;
            } else if (type === "mobile") {
                // Limpar URL temporária se existir
                if (
                    this.form.mobile_image &&
                    this.form.mobile_image.startsWith("blob:")
                ) {
                    URL.revokeObjectURL(this.form.mobile_image);
                }
                this.form.mobile_image = null;
                this.form.mobile_image_url = null;
                this.form.mobile_file = null;
            }
        },

        validateForm() {
            this.errors = {};

            // Strategy Pattern para validações
            const validationStrategies = {
                required: (value, fieldName, message) => {
                    if (
                        !value ||
                        (Array.isArray(value) && value.length === 0)
                    ) {
                        return message || `O campo ${fieldName} é obrigatório.`;
                    }
                    return null;
                },

                dateRange: (startDate, endDate) => {
                    if (
                        startDate &&
                        endDate &&
                        new Date(startDate) >= new Date(endDate)
                    ) {
                        return "A data de início deve ser anterior à data de fim.";
                    }
                    return null;
                },

                uniqueValues: (values, fieldName) => {
                    const duplicates = values.filter(
                        (value, index) => values.indexOf(value) !== index,
                    );
                    if (duplicates.length > 0) {
                        return `${fieldName} duplicado encontrado.`;
                    }
                    return null;
                },
            };

            // Configuração das validações principais
            const mainValidations = [
                {
                    field: "start_date",
                    value: this.form.start_date,
                    strategy: "required",
                    message: "A data de início é obrigatória.",
                },
                {
                    field: "end_date",
                    value: this.form.end_date,
                    strategy: "required",
                    message: "A data de início é obrigatória.",
                },
                {
                    field: "end_date",
                    value: this.form.end_date,
                    strategy: "required",
                    message: "A data de fim é obrigatória.",
                },
            ];

            // Executar validações principais
            let isValid = true;
            mainValidations.forEach((validation) => {
                const error = validationStrategies[validation.strategy](
                    validation.value,
                    validation.field,
                    validation.message,
                );
                if (error) {
                    this.errors[validation.field] = error;
                    isValid = false;
                }
            });

            // Validação de intervalo de datas
            const dateRangeError = validationStrategies.dateRange(
                this.form.start_date,
                this.form.end_date,
            );
            if (dateRangeError) {
                this.errors.end_date = dateRangeError;
                isValid = false;
            }

            // Validação das ofertas usando Strategy Pattern
            const offerValidationResult =
                this.validateOffers(validationStrategies);
            if (!offerValidationResult.isValid) {
                Object.assign(this.errors, offerValidationResult.errors);
                isValid = false;
            }

            return isValid;
        },

        validateOffers(strategies) {
            const errors = {};
            let isValid = true;
            const usedOfferIds = [];

            this.form.offers.forEach((offer, index) => {
                // Validar offer_id obrigatório
                const offerIdError = strategies.required(
                    offer.offer_id,
                    "Oferta",
                    "ID da oferta é obrigatório",
                );
                if (offerIdError) {
                    errors[`offers.${index}.offer_id`] = offerIdError;
                    isValid = false;
                } else {
                    usedOfferIds.push(offer.offer_id);
                }

                // Validar level_ids obrigatório
                const levelsError = strategies.required(
                    offer.level_ids,
                    "Níveis",
                    "Pelo menos um nível deve ser selecionado",
                );
                if (levelsError) {
                    errors[`offers.${index}.level_ids`] = levelsError;
                    isValid = false;
                }
            });

            return { isValid, errors };
        },

        clearErrors() {
            this.errors = {};
        },

        async submit() {
            if (!this.validateForm()) {
                this.toast.error("Por favor, corrija os erros no formulário");
                return;
            }

            this.loading = true;

            try {
                const campaignData = { ...this.form };
                const newArrayOffers = this.form.offers.map((offer) => {
                    return {
                        ...offer,
                    };
                });

                campaignData.offers = newArrayOffers;

                // Preparar dados para envio REST - enviar apenas as URLs das imagens
                if (this.form.desktop_image_url) {
                    campaignData.desktop_image_url =
                        this.form.desktop_image_url;
                }
                if (this.form.mobile_image_url) {
                    campaignData.mobile_image_url = this.form.mobile_image_url;
                }

                // Remover arquivos do envio (não são enviados via REST)
                delete campaignData.desktop_file;
                delete campaignData.mobile_file;

                // Fazer a requisição
                if (this.isEditing) {
                    const url = window.location.href;
                    const match = url.match(/\/campaign\/(\d+)/);
                    const campaignId = match[1];

                    await axios.put(
                        `/api/campaigns/${campaignId}`,
                        campaignData,
                    );

                    this.toast.success("Campanha atualizada com sucesso!");

                    this.goBack();

                    return;
                }

                await axios.post("/api/campaigns", campaignData);

                this.toast.success(
                    this.isEditing
                        ? "Campanha atualizada com sucesso!"
                        : "Campanha criada com sucesso!",
                );

                this.resetForm();
                this.goBack();
            } catch (error) {
                console.error("Erro ao salvar campanha:", error);

                let errorMessage = "Erro ao salvar campanha. Tente novamente.";

                if (
                    error.response &&
                    error.response.data &&
                    error.response.data.message
                ) {
                    errorMessage = error.response.data.message;
                } else if (
                    error.response &&
                    error.response.data &&
                    error.response.data.errors
                ) {
                    // Tratar erros de validação do backend
                    this.errors = error.response.data.errors;

                    errorMessage = "Verifique os campos destacados";
                }

                this.toast.error(errorMessage);
            } finally {
                this.loading = false;
            }
        },

        async store(file, options = null) {
            const response = await axios.post(
                "/vapor/signed-storage-url",
                {
                    bucket: options.bucket || "",
                    content_type: options.contentType || file.type,
                    expires: options.expires || "",
                    visibility: "public-read",
                    ...options.data,
                },
                {
                    // baseURL: options.baseURL || null,
                    ...options.options,
                },
            );

            if (response.status !== 200 && response.status !== 201) {
                throw new Error("Erro ao fazer upload.");
            }

            let headers = response.data.headers;

            if ("Host" in headers) {
                delete headers.Host;
            }

            if (typeof options.progress === "undefined") {
                options.progress = () => {};
            }

            const cancelToken = options.cancelToken || "";

            await axios.put(response.data.url, file, {
                cancelToken: cancelToken,
                headers: headers,
                withCredentials: false,
                onUploadProgress: (progressEvent) => {
                    options.progress(
                        progressEvent.loaded / progressEvent.total,
                    );
                },
            });

            response.data.extension = file.name.split(".").pop();

            return response.data;
        },
        uploadFile(
            fieldName,
            file,
            metadata,
            load,
            error,
            filePondProgress,
            abort,
            type,
            expectedWidth,
            expectedHeight,
        ) {
            if (file.type !== "image/png") {
                this.toast.error("A imagem deve ser do tipo PNG.");
                abort();
                this.handleDeleteImage(type);
                return;
            }

            // Criar URL temporário para preview imediato
            const tempImageUrl = URL.createObjectURL(file);

            // Atualizar o formulário com a imagem temporária imediatamente
            if (type === "desktop") {
                this.form.desktop_image = tempImageUrl;
                this.form.desktop_file = file;
            } else if (type === "mobile") {
                this.form.mobile_image = tempImageUrl;
                this.form.mobile_file = file;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    if (
                        img.width !== expectedWidth ||
                        img.height !== expectedHeight
                    ) {
                        this.toast.error(
                            `A imagem deve ter as dimensões ${expectedWidth}x${expectedHeight}.`,
                        );
                        abort();
                        this.handleDeleteImage(type);
                        return;
                    }

                    this.store(file, {
                        progress: async (progress) => {
                            filePondProgress(Math.round(progress * 100));
                        },
                        abort,
                    })
                        .then((response) => {
                            load(response);
                            if (type === "desktop") {
                                this.form.desktop_image_url = response?.key;
                                // Manter a imagem temporária para preview
                                // A URL real será usada quando a campanha for salva
                            } else if (type === "mobile") {
                                this.form.mobile_image_url = response?.key;
                                // Manter a imagem temporária para preview
                                // A URL real será usada quando a campanha for salva
                            }
                        })
                        .catch((error) => {
                            abort();
                            if (type === "desktop") {
                                this.form.desktop_image_url = null;
                                this.form.desktop_image = null;
                                this.form.desktop_file = null;
                            } else if (type === "mobile") {
                                this.form.mobile_image_url = null;
                                this.form.mobile_image = null;
                                this.form.mobile_file = null;
                            }
                        });
                };
                img.onerror = () => {
                    this.toast.error("Erro ao carregar a imagem.");
                    abort();
                    this.handleDeleteImage(type);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        },

        resetForm() {
            // Limpar URLs temporárias das imagens
            if (
                this.form.desktop_image &&
                this.form.desktop_image.startsWith("blob:")
            ) {
                URL.revokeObjectURL(this.form.desktop_image);
            }
            if (
                this.form.mobile_image &&
                this.form.mobile_image.startsWith("blob:")
            ) {
                URL.revokeObjectURL(this.form.mobile_image);
            }

            this.form = {
                start_date: "",
                end_date: "",
                comments: "",
                is_active: true,
                desktop_image: null,
                desktop_file: null,
                mobile_image: null,
                mobile_file: null,
                offers: [
                    {
                        offer_id: null,
                        selectedOffer: null,
                        level_ids: [],
                        status: "enabled",
                    },
                ],
            };
            this.errors = {};
            this.fetchOffers();
        },

        async fetchOffers() {
            try {
                const { data } = await axios.get(`/api/campaigns/offers`);

                this.listOffers = data;
            } catch (error) {
                console.log(error);
            }
        },

        async fetchLevels() {
            try {
                const { data } = await axios.get("/api/levels");

                this.levels = data.map((level) => ({
                    value: level.id,
                    label: level.label,
                }));
            } catch (error) {
                this.toast.error("Erro ao carregar níveis");
            }
        },

        async fetchCampaign(id) {
            try {
                const { data } = await axios.get(`/api/campaigns/${id}`);
                this.campaign = data;

                this.form = {
                    is_active: this.campaign.is_active,
                    start_date: this.formatDate(this.campaign.start_date),
                    end_date: this.formatDate(this.campaign.end_date),
                    comments: this.campaign.comments || "",
                    desktop_image: this.campaign.desktop_image_url,
                    mobile_image: this.campaign.mobile_image_url,
                    desktop_image_url: this.campaign.desktop_image_url,
                    mobile_image_url: this.campaign.mobile_image_url,
                    desktop_file: null,
                    mobile_file: null,
                    offers: this.campaign.offers.map((item) => ({
                        offer_id: item.offer_id,
                        selectedOffer: null, // Será definido após carregar as ofertas
                        level_ids: item.level_id,
                        status: item.status,
                    })) || [
                        {
                            offer_id: null,
                            selectedOffer: null,
                            level_ids: [],
                            status: "enabled",
                        },
                    ],
                };

                this.$nextTick(() => {
                    this.form.offers.forEach((offer) => {
                        if (offer.offer_id) {
                            offer.selectedOffer =
                                this.listOffers.find(
                                    (listOffer) =>
                                        listOffer.id === offer.offer_id,
                                ) || null;
                        }
                    });
                });

                this.toast.success("Campanha carregada com sucesso!");
            } catch (error) {
                console.log(error);

                this.toast.error("Erro ao carregar campanha");
            }
        },

        formatDate(date) {
            if (!date) return "";
            if (date.includes("-")) return date.slice(0, 10); // formato YYYY-MM-DD
            const [day, month, yearAndTime] = date.split("/");
            const [year] = yearAndTime.split(" ");
            return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        },
    },

    async created() {
        await this.fetchLevels();
    },

    async mounted() {
        // Carregar ofertas primeiro
        await this.fetchOffers();

        // Exemplo: carregar campanha para edição se houver ID na URL
        const url = window.location.href;
        const match = url.match(/\/campaign\/(\d+)/);
        const campaignId = match[1];

        if (campaignId) {
            this.isEditing = true;
            await this.fetchCampaign(campaignId);
        }
    },

    beforeUnmount() {
        // Limpar URLs temporárias das imagens quando o componente for destruído
        if (
            this.form.desktop_image &&
            this.form.desktop_image.startsWith("blob:")
        ) {
            URL.revokeObjectURL(this.form.desktop_image);
        }
        if (
            this.form.mobile_image &&
            this.form.mobile_image.startsWith("blob:")
        ) {
            URL.revokeObjectURL(this.form.mobile_image);
        }
    },
};
</script>
